import React, { useEffect, useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { Phone, ArrowLeft, MapPin, Plus, Minus } from 'lucide-react';
import { Navigation } from './Navigation';
export interface FAQItem {
  question: string;
  answer: string;
}
export interface BreadcrumbItem {
  name: string;
  item: string;
}
interface ServicePageLayoutProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  metaDescription: string;
  schema: object;
  /** Optional FAQ list. When provided, renders an accessible FAQ section and injects FAQPage schema. */
  faqs?: FAQItem[];
  /** Optional breadcrumb trail. When provided, renders a visible breadcrumb nav and injects BreadcrumbList schema. */
  breadcrumbs?: BreadcrumbItem[];
}
export function ServicePageLayout({
  title,
  subtitle,
  children,
  metaDescription,
  schema,
  faqs,
  breadcrumbs
}: ServicePageLayoutProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${title} | Mike's Pro Detail`;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', metaDescription);
    // Set canonical link based on current path
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://www.fulldetailpro.com${window.location.pathname}`);
    // Build the schema graph: the page's Service/primary schema, plus
    // optional FAQPage and BreadcrumbList schemas.
    const graph: object[] = [schema];
    if (faqs && faqs.length > 0) {
      graph.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(f => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.answer
          }
        }))
      });
    }
    if (breadcrumbs && breadcrumbs.length > 0) {
      graph.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.name,
          item: b.item
        }))
      });
    }
    const scripts: HTMLScriptElement[] = graph.map(obj => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      return s;
    });
    return () => {
      scripts.forEach(s => {
        if (document.head.contains(s)) {
          document.head.removeChild(s);
        }
      });
    };
  }, [title, metaDescription, schema, faqs, breadcrumbs]);
  return <main className="min-h-screen w-full bg-black text-white selection:bg-red-600 selection:text-white" data-id="element-424">
      <Navigation data-id="element-425" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-4 border-b border-zinc-900 overflow-hidden" data-id="element-426">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-red-900/20 blur-[120px] pointer-events-none rounded-full" data-id="element-427" />

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-428">
          {/* Breadcrumb navigation */}
          {breadcrumbs && breadcrumbs.length > 0 ? <nav aria-label="Breadcrumb" className="mb-8" data-id="element-429">
              <ol className="flex flex-wrap items-center gap-2 text-sm text-gray-400" data-id="element-430">
                {breadcrumbs.map((b, i) => <li key={b.item} className="flex items-center gap-2" data-id="element-431">
                    {i < breadcrumbs.length - 1 ? <a href={b.item.replace('https://www.fulldetailpro.com', '')} className="hover:text-white transition-colors" data-id="element-432">
                        {b.name}
                      </a> : <span className="text-gray-300" aria-current="page" data-id="element-433">
                        {b.name}
                      </span>}
                    {i < breadcrumbs.length - 1 && <span className="text-zinc-600" data-id="element-434">/</span>}
                  </li>)}
              </ol>
            </nav> : <a href="/" className="inline-flex items-center text-gray-400 hover:text-white mb-8 transition-colors group" data-id="element-435">
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-436" />
              Back to Home
            </a>}

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-437">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight mb-6" data-id="element-438">
              <span className="bg-clip-text text-transparent bg-gradient-to-b from-yellow-300 via-yellow-400 to-yellow-500 drop-shadow-lg" data-id="element-439">
                {title}
              </span>
            </h1>
            <div className="h-1 w-24 bg-red-600 rounded-full shadow-[0_0_10px_rgba(220,38,38,0.8)] mb-6" data-id="element-440" />
            <p className="text-xl text-gray-400 leading-relaxed max-w-3xl" data-id="element-441">
              {subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Area */}
      <section className="py-16 px-4" data-id="element-442">
        <div className="max-w-4xl mx-auto" data-id="element-443">
          <div className="prose prose-invert prose-lg max-w-none prose-headings:font-black prose-headings:uppercase prose-headings:tracking-tight prose-h2:text-3xl prose-h2:mb-6 prose-h2:mt-12 prose-h3:text-2xl prose-h3:text-yellow-400 prose-p:text-gray-300 prose-p:leading-relaxed prose-li:text-gray-300 prose-strong:text-white" data-id="element-444">
            {children}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {faqs && faqs.length > 0 && <section className="py-16 px-4 border-t border-zinc-900" aria-labelledby="faq-heading" data-id="element-445">
          <div className="max-w-4xl mx-auto" data-id="element-446">
            <h2 id="faq-heading" className="text-3xl md:text-4xl font-black uppercase tracking-tight mb-8 text-center" data-id="element-447">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4" data-id="element-448">
              {faqs.map((faq, index) => {
            const isOpen = openFaq === index;
            return <div key={index} className="bg-zinc-900/60 border border-zinc-800 rounded-xl overflow-hidden" data-id="element-449">
                    <button onClick={() => setOpenFaq(isOpen ? null : index)} className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-zinc-800/40 transition-colors" aria-expanded={isOpen} data-id="element-450">
                      <span className="text-lg font-bold text-white" data-id="element-451">
                        {faq.question}
                      </span>
                      {isOpen ? <Minus className="w-5 h-5 text-red-500 shrink-0" data-id="element-452" /> : <Plus className="w-5 h-5 text-red-500 shrink-0" data-id="element-453" />}
                    </button>
                    {isOpen && <div className="px-5 pb-5 text-gray-300 leading-relaxed" data-id="element-454">
                        {faq.answer}
                      </div>}
                  </div>;
          })}
            </div>
          </div>
        </section>}

      {/* Service Areas SEO Block */}
      <section className="py-12 px-4 bg-zinc-900/30 border-t border-zinc-900" data-id="element-455">
        <div className="max-w-4xl mx-auto text-center" data-id="element-456">
          <MapPin className="w-8 h-8 text-red-500 mx-auto mb-4" data-id="element-457" />
          <h2 className="text-2xl font-bold mb-4" data-id="element-458">
            Proudly Serving Ventura County
          </h2>
          <p className="text-gray-400 leading-relaxed" data-id="element-459">
            We provide top-tier mobile auto detailing in{' '}
            <a href="/ventura-county/camarillo" className="text-red-500 hover:underline" data-id="element-460">
              Camarillo
            </a>
            ,{' '}
            <a href="/ventura-county/oxnard" className="text-red-500 hover:underline" data-id="element-461">
              Oxnard
            </a>
            ,{' '}
            <a href="/ventura-county/ventura" className="text-red-500 hover:underline" data-id="element-462">
              Ventura
            </a>
            ,{' '}
            <a href="/ventura-county/thousand-oaks" className="text-red-500 hover:underline" data-id="element-463">
              Thousand Oaks
            </a>
            ,{' '}
            <a href="/ventura-county/simi-valley" className="text-red-500 hover:underline" data-id="element-464">
              Simi Valley
            </a>
            ,{' '}
            <a href="/ventura-county/moorpark" className="text-red-500 hover:underline" data-id="element-465">
              Moorpark
            </a>
            ,{' '}
            <a href="/ventura-county/newbury-park" className="text-red-500 hover:underline" data-id="element-466">
              Newbury Park
            </a>
            , and{' '}
            <a href="/ventura-county/westlake-village" className="text-red-500 hover:underline" data-id="element-467">
              Westlake Village
            </a>
            . Whether you need car detailing in Camarillo or mobile detailing in
            Ventura County, we come directly to your home or office.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden border-t border-zinc-900" data-id="element-468">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/40 to-black pointer-events-none" data-id="element-469" />
        <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-470">
          <h2 className="text-4xl md:text-5xl font-black uppercase mb-6" data-id="element-471">
            Ready to Transform Your Vehicle?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto" data-id="element-472">
            Book your professional mobile detailing appointment today. We bring
            the shop to you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-473">
            <a href="tel:8183819905" className="flex items-center gap-3 px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold text-xl rounded-sm transition-all shadow-[0_0_20px_rgba(220,38,38,0.5)] hover:shadow-[0_0_30px_rgba(220,38,38,0.8)] hover:-translate-y-1 w-full sm:w-auto justify-center" data-id="element-474">
              <Phone className="w-6 h-6" data-id="element-475" />
              (818) 381-9905
            </a>
            <a href="/#contact-form" className="px-8 py-4 bg-white text-black hover:bg-gray-200 font-bold text-xl uppercase tracking-wider rounded-sm transition-all w-full sm:w-auto text-center" data-id="element-476">
              Get a Free Quote
            </a>
          </div>
        </div>
      </section>
    </main>;
}